import { f0FormField } from '@factorialco/f0-react'
import { z } from 'zod'

import i18n from 'modules/core/lib/i18n'
import { recurrenceLabel, type DefaultRecurrence } from 'modules/incentives/lib/recurrence'
import { useMultiTeamSelectorFormField } from 'modules/core/lib/formFields/teamSelector'
import { useMultiLegalEntitySelectorFormField } from 'modules/core/lib/formFields/legalEntitySelector'
import { useSinglePayrollConceptSelectorFormField } from 'modules/incentives/lib/formFields/payrollConceptSelector'
import { SUPPORTED_RECURRENCES } from 'modules/incentives/lib/recurrence'

import moment from 'moment'

const FILTER_SCOPE_VALUES = ['all_company', 'team', 'legal_entity'] as const

export type BasicsStepData = z.infer<ReturnType<typeof createBasicsSchema>>

export const RECURRENCE_TO_DATE_FIELD = {
  monthly: 'startsOnMonthly',
  every_3_months: 'startsOnQuarterly',
  every_6_months: 'startsOnSemiAnnual',
  every_12_months: 'startsOnAnnual',
} as const satisfies Record<DefaultRecurrence, keyof BasicsStepData>

interface CreateBasicsSchemaParams {
  payrollConceptId: ReturnType<typeof useSinglePayrollConceptSelectorFormField>
  teamIds: ReturnType<typeof useMultiTeamSelectorFormField>
  legalEntityIds: ReturnType<typeof useMultiLegalEntitySelectorFormField>
}

export const createBasicsSchema = ({
  payrollConceptId,
  teamIds,
  legalEntityIds,
}: CreateBasicsSchemaParams) =>
  z
    .object({
      name: f0FormField(z.string().trim().min(1), {
        label: i18n.t('incentives.wizard.basics.name.label'),
      }),
      payrollConceptId,
      recurrence: f0FormField(z.enum(SUPPORTED_RECURRENCES), {
        label: i18n.t('incentives.wizard.basics.recurrence.label'),
        fieldType: 'select',
        options: SUPPORTED_RECURRENCES.map((value) => ({
          value,
          label: recurrenceLabel(value),
        })),
        row: 'frequency-selector-row',
      }),
      startsOnMonthly: f0FormField(z.date().optional(), {
        label: i18n.t('incentives.wizard.basics.starts_on.label'),
        fieldType: 'date',
        granularities: ['month'],
        minDate: moment().startOf('day').toDate(),
        renderIf: { fieldId: 'recurrence', equalsTo: 'monthly' },
        row: 'frequency-selector-row',
      }),
      startsOnQuarterly: f0FormField(z.date().optional(), {
        label: i18n.t('incentives.wizard.basics.starts_on.label'),
        fieldType: 'date',
        granularities: ['quarter'],

        renderIf: { fieldId: 'recurrence', equalsTo: 'every_3_months' },
        row: 'frequency-selector-row',
      }),
      startsOnSemiAnnual: f0FormField(z.date().optional(), {
        label: i18n.t('incentives.wizard.basics.starts_on.label'),
        fieldType: 'date',
        granularities: ['halfyear'],
        minDate: moment().startOf('day').toDate(),
        renderIf: { fieldId: 'recurrence', equalsTo: 'every_6_months' },
        row: 'frequency-selector-row',
      }),
      startsOnAnnual: f0FormField(z.date().optional(), {
        label: i18n.t('incentives.wizard.basics.starts_on.label'),
        fieldType: 'date',
        granularities: ['year'],
        minDate: moment().startOf('day').toDate(),
        renderIf: { fieldId: 'recurrence', equalsTo: 'every_12_months' },
        row: 'frequency-selector-row',
      }),
      filterScope: f0FormField(z.enum(FILTER_SCOPE_VALUES), {
        label: i18n.t('incentives.wizard.basics.filter_scope.label'),
        placeholder: i18n.t('incentives.wizard.basics.filter_scope.placeholder'),
        helpText: i18n.t('incentives.wizard.basics.filter_scope.helpText'),
        fieldType: 'select',
        row: 'filter-scope-row',
        options: [
          {
            value: 'all_company' as const,
            label: i18n.t('incentives.wizard.basics.filter_scope.options.all_company'),
          },
          {
            value: 'team' as const,
            label: i18n.t('incentives.wizard.basics.filter_scope.options.team'),
          },
          {
            value: 'legal_entity' as const,
            label: i18n.t('incentives.wizard.basics.filter_scope.options.legal_entity'),
          },
        ],
      }),
      teamIds,
      legalEntityIds,
    })
    .superRefine((values, ctx) => {
      const fieldName = RECURRENCE_TO_DATE_FIELD[values.recurrence]
      if (!values[fieldName]) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: i18n.t('incentives.wizard.basics.starts_on.validation.required'),
          path: [fieldName],
        })
      }
    })
